import type { IntlShape } from 'react-intl';
import type { AppVisibility } from '@api/apps';
import type { UserAppStatus } from '@api/appBuilder';
import { BUILDER_RUN_PARAM } from './workspace/builderWorkspace.constants';

interface StatusSource {
  readonly status: UserAppStatus;
  readonly visibility: AppVisibility;
  readonly publishedVersion?: number | null;
}

/** "Draft", "Only me · v3", "Shared · v3" or "Disabled by AIBuild". */
export const userAppStatusLabel = (
  intl: IntlShape,
  { status, visibility, publishedVersion }: StatusSource,
): string => {
  switch (status) {
    case 'DRAFT':
      return intl.formatMessage({ id: 'apps.builder.status.draft', defaultMessage: 'Draft' });
    case 'DISABLED':
      return intl.formatMessage({
        id: 'apps.builder.status.disabled',
        defaultMessage: 'Disabled by AIBuild',
      });
    case 'PUBLISHED':
      return visibility === 'ORG'
        ? intl.formatMessage(
            { id: 'apps.builder.status.shared', defaultMessage: 'Shared · v{version}' },
            { version: publishedVersion },
          )
        : intl.formatMessage(
            { id: 'apps.builder.status.private', defaultMessage: 'Only me · v{version}' },
            { version: publishedVersion },
          );
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
};

/** Whether opening the app runs it; otherwise there is nothing to run yet and it opens in the builder. */
export const isRunnable = ({ status, publishedVersion }: StatusSource): boolean =>
  status === 'PUBLISHED' && publishedVersion != null;

export const APP_BUILDER_ROUTE = '/apps/builder/:appKey';

export const builderPath = (appKey: string): string =>
  `/apps/builder/${encodeURIComponent(appKey)}`;

/** The builder showing one of its test runs in place. */
export const builderRunPath = (appKey: string, runId: string): string =>
  `${builderPath(appKey)}?${new URLSearchParams({ [BUILDER_RUN_PARAM]: runId }).toString()}`;
